/**
 * Lagerorte verwalten.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from '../components/toast';
import { Alert, Badge, Dialog, Field, LoadingBlock, TextInput } from '../components/ui';

export type Warehouse = {
  id: string;
  code: string;
  name: string;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  isDefault: boolean;
  active: boolean;
  itemCount: number;
  movementCount: number;
};

export function WarehousesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('settings:write');

  const [editing, setEditing] = useState<Warehouse | 'neu' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<{ warehouses: Warehouse[] }>('/api/stock/warehouses'),
  });

  const update = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<Warehouse> }) =>
      api.patch(`/api/stock/warehouses/${id}`, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      toast.success('Gespeichert.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Änderung fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean; message?: string }>(`/api/stock/warehouses/${id}`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      toast[result.deleted ? 'success' : 'info'](result.message ?? 'Der Lagerort wurde gelöscht.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  if (isLoading || !data) return <LoadingBlock />;

  return (
    <>
      <Alert kind="info">
        Jede Lagerbewegung gehört zu einem Lagerort. Das Vorgabelager wird verwendet, wenn an
        einem Beleg nichts anderes gewählt ist. Ein Lagerort mit Bewegungshistorie wird nicht
        gelöscht, sondern deaktiviert — die Historie muss lesbar bleiben.
      </Alert>

      <div className="card">
        <div className="card-header">
          <h2>{data.warehouses.length} Lagerorte</h2>
          {mayWrite ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('neu')}>
              Lagerort anlegen
            </button>
          ) : null}
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="shrink">Kürzel</th>
                <th>Bezeichnung</th>
                <th>Anschrift</th>
                <th className="numeric">Artikel</th>
                <th className="numeric">Bewegungen</th>
                <th className="shrink">Status</th>
                <th className="shrink"></th>
              </tr>
            </thead>
            <tbody>
              {data.warehouses.map((warehouse) => (
                <tr key={warehouse.id}>
                  <td className="mono">{warehouse.code}</td>
                  <td style={{ fontWeight: 500 }}>{warehouse.name}</td>
                  <td className="muted">
                    {warehouse.street}
                    {warehouse.street && warehouse.city ? ', ' : ''}
                    {warehouse.postalCode} {warehouse.city}
                  </td>
                  <td className="numeric">{warehouse.itemCount || ''}</td>
                  <td className="numeric muted">{warehouse.movementCount || ''}</td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      {warehouse.isDefault ? <Badge kind="accent">Vorgabe</Badge> : null}
                      {!warehouse.active ? <Badge kind="warning">Inaktiv</Badge> : null}
                    </div>
                  </td>
                  <td>
                    {mayWrite ? (
                      <div className="row" style={{ gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setEditing(warehouse)}
                        >
                          Ändern
                        </button>
                        {!warehouse.isDefault ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() =>
                                update.mutate({ id: warehouse.id, changes: { isDefault: true } })
                              }
                            >
                              Als Vorgabe
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => remove.mutate(warehouse.id)}
                            >
                              Löschen
                            </button>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <WarehouseDialog
          warehouse={editing === 'neu' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function WarehouseDialog({
  warehouse,
  onClose,
}: {
  warehouse: Warehouse | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    code: warehouse?.code ?? '',
    name: warehouse?.name ?? '',
    street: warehouse?.street ?? '',
    postalCode: warehouse?.postalCode ?? '',
    city: warehouse?.city ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        street: form.street || null,
        postalCode: form.postalCode || null,
        city: form.city || null,
      };
      return warehouse
        ? api.patch(`/api/stock/warehouses/${warehouse.id}`, payload)
        : api.post('/api/stock/warehouses', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      toast.success('Gespeichert.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.error(error.message);
      } else {
        toast.error('Speichern fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={warehouse ? `Lagerort ${warehouse.code}` : 'Lagerort anlegen'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !form.code.trim() || !form.name.trim()}
            onClick={() => {
              setErrors({});
              save.mutate();
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Kürzel" span={4} required error={errors.code}>
          <TextInput
            value={form.code}
            onChange={(value) => setForm({ ...form, code: value.toUpperCase() })}
            invalid={Boolean(errors.code)}
            autoFocus
          />
        </Field>
        <Field label="Bezeichnung" span={8} required error={errors.name}>
          <TextInput
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
            invalid={Boolean(errors.name)}
          />
        </Field>
        <Field label="Straße" span={6} error={errors.street}>
          <TextInput value={form.street} onChange={(value) => setForm({ ...form, street: value })} />
        </Field>
        <Field label="PLZ" span={2} error={errors.postalCode}>
          <TextInput
            value={form.postalCode}
            onChange={(value) => setForm({ ...form, postalCode: value })}
          />
        </Field>
        <Field label="Ort" span={4} error={errors.city}>
          <TextInput value={form.city} onChange={(value) => setForm({ ...form, city: value })} />
        </Field>
      </div>
    </Dialog>
  );
}
